import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const TagList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tags`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getTags = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching tags with params:", requestParams);
        const response = await masterService.getTagList(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Tags API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const tagsData = response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Tags data:", tagsData);
          console.log("Pagination data:", paginationData);

          // Ensure tagsData is an array
          if (Array.isArray(tagsData)) {
            setTags(tagsData);
          } else {
            console.error("Tags data is not an array:", tagsData);
            setTags([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch tags" });
          setTags([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching tags:", error);
        notify.error({ message: "Failed to fetch tags" });
        setTags([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load tags data. Mount only: every later fetch is an explicit getTags(...) from
  // a search or pagination handler. Re-running this whenever getTags is rebuilt
  // would fire a second, redundant request alongside each of them — and one per
  // keystroke, which is what the debounce below exists to stop.
  useEffect(() => {
    setPageTitle("Tags");
    getTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getTags({
        searchText: value.trim(),
        currentPage: 1,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getTags({ searchText: (filters.searchText || "").trim(), currentPage: 1 });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getTags({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add tag
  const handleAddTag = () => {
    navigate("/tags/add/new");
  };

  // Handle edit tag
  const handleEditTag = (tagId) => {
    navigate(`/tags/edit/${tagId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Tag Name",
      dataIndex: "tag_name",
      key: "tag_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.tag_name.localeCompare(b.tag_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "tag_is_active",
      key: "tag_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTag(record.tag_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Tags ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search tags..."
              allowClear
              value={filters.searchText}
              onChange={handleSearchChange}
              onSearch={handleSearchNow}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTag}
              size="middle"
            >
              Add Tag
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={tags}
          rowKey="tag_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No tags found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default TagList;
